import { useAuth, useAuthState } from '#imports';

/**
 * How old the session has to be before a foreground refresh is allowed through.
 *
 * The app shell endpoint (`GET /v2/user/app`) costs a double-digit number of queries per call, and
 * sidebase's default handler refreshes on every `visibilitychange` → visible. On a PWA that is
 * every pocket-to-hand, so during an event a single attendee generates a stream of identical
 * session fetches. At ~5,000 attendees that is the dominant source of load on the endpoint.
 *
 * A minute of staleness is safe here because nothing depends on the foreground refresh to see a
 * change it caused:
 *  - every mutation that changes session-backed data refreshes explicitly right after it (the
 *    settings cards, ticket linking, email verification, OAuth linking, onboarding)
 *  - permission changes made elsewhere are pushed over SSE as `reload-permissions` (app.vue)
 *  - a missing `chat_auth_token` is refetched on demand by the attendee contact-details page
 */
const SESSION_STALE_AFTER_MS = 60_000;

/**
 * Replaces sidebase's `DefaultRefreshHandler` (node_modules/@sidebase/nuxt-auth/dist/runtime/
 * utils/refreshHandler.js) so a foreground only refreshes the session when it is actually stale.
 *
 * Wired up via `auth.sessionRefresh.handler` in nuxt.config.ts. Note that once a custom handler is
 * configured, sidebase stops applying `sessionRefresh.enableOnWindowFocus` and
 * `enablePeriodically` — the module only passes those to its own default handler — so the whole
 * refresh policy lives in this file. Periodic refresh is deliberately not implemented: it was off
 * before and nothing wants it.
 *
 * Only the *automatic* foreground refresh goes through here. Explicit `getSession()` calls — route
 * guards on a hard load, and the post-mutation refreshes listed above — are untouched by design
 * and must stay that way: they follow a known write and have to show its result immediately.
 */
const createThrottledRefreshHandler = () => {
  // Resolved in init() rather than on use: these are Nuxt composables, and the visibilitychange
  // callback runs with no Nuxt instance active.
  let auth: ReturnType<typeof useAuth> | undefined;
  let lastRefreshedAt: ReturnType<typeof useAuthState>['lastRefreshedAt'] | undefined;

  const isSessionStale = (): boolean => {
    const refreshedAt = lastRefreshedAt?.value;

    // Undefined means no fetch has been recorded for this session yet - treat as stale rather
    // than skipping, so a restored session still gets validated on the first foreground.
    if (!refreshedAt) return true;

    return Date.now() - refreshedAt.getTime() >= SESSION_STALE_AFTER_MS;
  };

  // `lastRefreshedAt` is only stamped once a fetch comes back, so it cannot throttle events that
  // arrive while one is still in flight - a burst of foreground events would each see the same
  // stale timestamp and start their own request. Tracked separately for that reason.
  let refreshing = false;

  // One reference for the life of the handler, so destroy() can remove the listener again.
  const onVisibilityChange = () => {
    if (document.visibilityState !== 'visible') return;

    // No session yet (or signed out): the boot fetch and the sign-in flow own that case.
    if (!auth?.data.value) return;

    if (refreshing || !isSessionStale()) return;

    refreshing = true;
    auth
      .refresh()
      // A foreground refresh that fails is not actionable - the session the app already has stays
      // usable and the next foreground tries again - so it is swallowed rather than left to
      // surface as an unhandled rejection in Bugsnag. `finally` alone does not handle it.
      .catch(() => {})
      // Released whether or not the refresh succeeded, so a failure cannot wedge the handler shut
      // until the next page load.
      .finally(() => {
        refreshing = false;
      });
  };

  return {
    init() {
      auth = useAuth();
      // `lastRefreshedAt` is stamped inside getSession itself, so it also covers the guard fetches
      // and the explicit post-mutation refreshes - a foreground right after a settings save sees a
      // fresh session and correctly does nothing.
      lastRefreshedAt = useAuthState().lastRefreshedAt;

      document.addEventListener('visibilitychange', onVisibilityChange);
    },

    destroy() {
      document.removeEventListener('visibilitychange', onVisibilityChange);

      auth = undefined;
      lastRefreshedAt = undefined;
      refreshing = false;
    },
  };
};

// sidebase imports this module's DEFAULT export and calls init()/destroy() on it directly, so what
// is exported has to be a ready-made handler, not something the caller still has to construct.
export default createThrottledRefreshHandler();
